/**
 * Create an organization.
 *
 * The build instruction says not to require a consultant to finish a setup
 * wizard before they record useful work. The organization therefore exists
 * after this one short form. Everything else is a checklist on the settings
 * page, and a missing prerequisite is named again when an assessment is
 * finalized.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  Button,
  Field,
  Loading,
  Note,
  PageHeader,
  Panel,
  Problem,
  inputClass,
} from "../components/primitives.tsx";
import { api } from "../lib/api.ts";
import { useProblem } from "../lib/problem.ts";

interface CreatableView {
  readonly allowed: boolean;
  readonly platformAdmin: boolean;
  readonly providers: readonly { tenantId: string; name: string; slug: string }[];
  readonly identityReady: boolean;
}

interface CreatedView {
  readonly tenantId: string;
  readonly slug: string;
  readonly name: string;
  readonly ownerEmail: string;
  readonly ownerIsCreator: boolean;
}

/** Suggest a short name from the organization's name. */
function slugFrom(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function NewOrganizationPage({ first = false }: { readonly first?: boolean }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [kind, setKind] = useState("customer");
  const [ownMine, setOwnMine] = useState(true);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [providerTenantId, setProviderTenantId] = useState("");
  const { problem, setProblem, onError } = useProblem();
  const [created, setCreated] = useState<CreatedView | null>(null);

  const queryClient = useQueryClient();
  const creatable = useQuery({
    queryKey: ["creatable"],
    queryFn: () => api.get<CreatableView>("/api/organizations/creatable"),
    retry: false,
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<CreatedView>("/api/organizations", body),
    onSuccess: async (row) => {
      setProblem(null);
      setCreated(row);
      // The switcher lists the organizations of the session.
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError,
  });

  if (creatable.isLoading) {
    return <Loading what="what you may create" />;
  }

  if (created !== null) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={`${created.name} is ready`} />
        <Panel title="What happens next">
          <p className="mb-3">
            {created.ownerIsCreator
              ? "You own this organization. Select it to start."
              : `ArcGRC invited ${created.ownerEmail} to own this organization. ` +
                "They register a passkey, then they sign in."}
          </p>
          <p className="text-sm text-muted">
            The settings page lists what the organization still needs. You can
            start a draft assessment before that list is complete.
          </p>
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={first ? "Make your first organization" : "Create an organization"} />

      <Note>
        {first
          ? "You belong to no organization yet. Make one to own it, then " +
            "invite its people."
          : "This form makes the organization and gives it an owner. The " +
            "rest of the setup is a checklist in Settings."}
      </Note>

      {creatable.data?.identityReady === false && !first && (
        <Problem
          message={
            "This server cannot invite a new owner. Ask the operator to set " +
            "the identity client."
          }
        />
      )}

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({
            name,
            slug: slug === "" ? slugFrom(name) : slug,
            kind,
            ...(!first && !ownMine ? { ownerEmail, ownerName } : {}),
            ...(providerTenantId === "" ? {} : { providerTenantId }),
          });
        }}
      >
        <Field id="org-name" label="Name">
          <input
            id="org-name"
            className={inputClass}
            required
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (!slugEdited) {
                setSlug(slugFrom(event.target.value));
              }
            }}
          />
        </Field>

        <Field id="org-slug" label="Short name">
          <input
            id="org-slug"
            className={inputClass}
            required
            value={slug}
            onChange={(event) => {
              setSlugEdited(true);
              setSlug(event.target.value);
            }}
          />
          <p className="mt-1 text-sm text-muted">
            Lowercase letters, numbers, and single hyphens. It never changes.
          </p>
        </Field>

        <Field id="org-kind" label="Kind">
          <select
            id="org-kind"
            className={inputClass}
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          >
            <option value="customer">Customer: it owns its own records</option>
            <option value="provider">Provider: it serves other organizations</option>
            <option value="assessor">Assessor: it reviews other organizations</option>
          </select>
        </Field>

        {(creatable.data?.providers.length ?? 0) > 0 && (
          <Field id="org-provider" label="Served by">
            <select
              id="org-provider"
              className={inputClass}
              value={providerTenantId}
              onChange={(event) => setProviderTenantId(event.target.value)}
            >
              <option value="">No provider</option>
              {(creatable.data?.providers ?? []).map((row) => (
                <option key={row.tenantId} value={row.tenantId}>
                  {row.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-muted">
              A relationship records who serves this organization. It gives no
              access.
            </p>
          </Field>
        )}

        {!first && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={ownMine}
              onChange={(event) => setOwnMine(event.target.checked)}
            />
            <span>
              I own this organization.
              <span className="block text-muted">
                Clear this to hand the organization to someone else. ArcGRC
                invites them, and you get no membership.
              </span>
            </span>
          </label>
        )}

        {!first && !ownMine && (
          <>
            <Field id="owner-email" label="Owner email address">
              <input
                id="owner-email"
                type="email"
                className={inputClass}
                required
                value={ownerEmail}
                onChange={(event) => setOwnerEmail(event.target.value)}
              />
            </Field>
            <Field id="owner-name" label="Owner name">
              <input
                id="owner-name"
                className={inputClass}
                required
                value={ownerName}
                onChange={(event) => setOwnerName(event.target.value)}
              />
            </Field>
          </>
        )}

        {problem !== null && <Problem message={problem} />}

        <Button type="submit" variant="primary" disabled={create.isPending}>
          {create.isPending ? "Creating" : "Create the organization"}
        </Button>
      </form>
    </div>
  );
}
